import * as React from "react";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {Link} from "react-router-dom";
import {
    findCardToReuse,
    getAmountToDisplay,
    getProductByPolicyNumber,
    getSessionStorage,
    setSessionStorage,
    isCardExpired
} from "../../../../../CommonFunctions";
import {
    AnnualPolicyPremiumTypeEnum,
    FalconNavigatorHeader,
    ICacheManager,
    ICustomerPortalService,
    Party,
    PaymentCard,
    Policy,
    PolicyProperty,
    PolicyPropertyEnum,
    ProductsEnum,
    Types
} from "@falcon/services";
import {lazyInject} from "@athena-portal/core";
import FalconFooter from "../../../../../components/navigation/FalconFooter";
import {PanelDashed} from "../../../../../components/PanelDashed";
import {FailedPaymentMessage} from "../../../../../components/payments/FailedPaymentMessage";

interface IParams {
    policyNumber: string
    invoiceId: string
    [key: string]: string | undefined
}

interface IState {
    premiumType?: string,
    dataLoaded: boolean,
    policy?: Policy,
    directDebitSupported: boolean,
    amountToPay: number,
    party?: Party,
    reuseCard?: boolean,
    mainCardId?: number,
}

@authorize
export class PayOverdueInstalmentPagePlain extends React.Component<IWithRouterProps<IParams>, IState> {
    @lazyInject(Types.CustomerPortalService)
    private readonly customerPortalService: ICustomerPortalService;

    @lazyInject(Types.CacheManager)
    private readonly cacheManager: ICacheManager;

    private readonly pluginTemplates = new PluginTemplates();

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.state = {
            dataLoaded: false,
            directDebitSupported: false,
            amountToPay: 0,
        };

        this.handleOnClick = this.handleOnClick.bind(this);
    }

    public componentDidMount(): void {
        this.pluginTemplates.setPageTitle("Payment");
        // Adding additional failsafe for cases where browser refreshes session but continues with the page
        // Guess would be - some android behaviour when app is in background for some time
        const product: string = this.props.location?.state?.product ?? getSessionStorage("missedPaymentProduct");
        if (product) {
            setSessionStorage("missedPaymentProduct", product);
        } else {
            this.customerPortalService.getPolicies().subscribe(policies => {
                const currentPolicy = policies.filter(policy => policy.policyNumber === this.props.params.policyNumber)[0];
                setSessionStorage("missedPaymentProduct", currentPolicy.interaction.product);
            });
        }
        setSessionStorage("isChargeBack", false);
        setSessionStorage("missedPaymentPolicyNumber", this.props.params.policyNumber);
        this.cacheManager.clearCache("getPolicyHistory");

        this.customerPortalService.getProfile().subscribe((output: Party) => {
            this.setState({
                party: output
            })
        });

        const policyNumber: string = this.props.params.policyNumber;

        this.customerPortalService
            .getPolicy(policyNumber)
            .subscribe((policy: Policy) => {
                    this.cacheManager.clearCache("getPolicy".concat(policy.policyNumber));
                    this.setState({
                        policy,
                        amountToPay: this.getAmountToPay(policy)
                    });
                }
            );
        this.customerPortalService.getPolicyPremiumTypeByPolicyNumber(this.props.params.policyNumber).subscribe((result: string) => {
            this.setState({
                premiumType: result,
                dataLoaded: true
            })
        });

        if (this.props.params.policyNumber) {
            if (getProductByPolicyNumber(this.props.params.policyNumber) === ProductsEnum.HOME) {
                this.setState({
                    directDebitSupported: true
                });
                this.customerPortalService.getPolicyPropertiesByPropertyKeys(
                    this.props.params.policyNumber,
                    [
                        PolicyPropertyEnum.REUSE_MAIN_CARD,
                        PolicyPropertyEnum.MAIN_ANNUAL_PAYMENT_CARD,
                    ]
                ).subscribe((properties: PolicyProperty[]) => {
                    const newState: any = {};
                    properties.forEach(property => {
                        if (property.key === PolicyPropertyEnum.REUSE_MAIN_CARD) {
                            newState.reuseCard = property.value === "true";
                        } else if (property.key === PolicyPropertyEnum.MAIN_ANNUAL_PAYMENT_CARD) {
                            newState.mainCardId = +property.value
                        }
                    });
                    this.setState(newState)
                });
            } else {
                this.customerPortalService.getPolicyPropertiesByPropertyKeys(this.props.params.policyNumber,
                    [PolicyPropertyEnum.REUSE_MAIN_CARD, PolicyPropertyEnum.MAIN_ANNUAL_PAYMENT_CARD, PolicyPropertyEnum.DIRECT_DEBIT_SUPPORTED]).subscribe(
                    (properties: PolicyProperty[]) => {
                        const newState: any = {};
                        properties.forEach(property => {
                            if (property.key === PolicyPropertyEnum.REUSE_MAIN_CARD) {
                                newState.reuseCard = property.value === "true";
                            } else if (property.key === PolicyPropertyEnum.MAIN_ANNUAL_PAYMENT_CARD) {
                                newState.mainCardId = +property.value;
                            } else if (property.key === PolicyPropertyEnum.DIRECT_DEBIT_SUPPORTED) {
                                newState.directDebitSupported = property.value === "true";
                            }
                        });
                        this.setState(newState);
                    }
                );
            }
        }
    }

    public render(): React.ReactNode {
        const dataLoaded = this.state.dataLoaded;
        return (
            <React.Fragment>
                <Overlay show={!dataLoaded}/>
                <FalconHeader/>
                {dataLoaded && this.renderPageContents()}
                <FalconFooter/>
            </React.Fragment>
        );
    }

    private getRedirectUrl = (): string => {
        const product: string = this.props.location?.state?.product ?? getSessionStorage("missedPaymentProduct");
        let redirectUrl: string;

        window.sessionStorage.removeItem("paymentAmount");

        if (product === ProductsEnum.WOLF_MOTOR || product === ProductsEnum.HOME) {
            redirectUrl = this.state.premiumType === AnnualPolicyPremiumTypeEnum.MONTHLY
                ? `/dashboard/monthlyPaymentHistory/${this.props.params.policyNumber}`
                : `/dashboard/annualPaymentHistory/${this.props.params.policyNumber}`;
        } else {
            redirectUrl = "/dashboard/paymentHistory";
        }

        return redirectUrl;
    }

    private readonly getAmountToPay = (policy: Policy): number => {
        const annualInfo = policy?.annualPolicyInfo;
        if (this.props.params.invoiceId) {
            return annualInfo?.unpaidInstallmentIdToAmount[this.props.params.invoiceId];
        } else {
            return annualInfo?.overdueMonthlyInvoiceAmount || annualInfo?.overdueAnnualInvoiceAmount || 0;
        }
    }

    private renderPageContents(): React.ReactNode {
        const product: string = this.props.location?.state?.product ?? getSessionStorage("missedPaymentProduct");
        const mainCard: PaymentCard | null = this.findMainPaymentCard();
        let cardExpired = undefined;
        if (mainCard) {
            cardExpired = isCardExpired(mainCard);
        }

        return (
            <div className="plugin">
                {this.props.location?.state?.verifyCardFailed && <FailedPaymentMessage directDebitSupported={this.state.directDebitSupported}/>}
                <FalconNavigatorHeader back={this.getRedirectUrl()} title="Pay overdue instalment"/>
                <div className="panel">
                    <div className="currency-amount-display price font-family-display font-weight-bold text-center">
                        {getAmountToDisplay(this.state.amountToPay)}
                    </div>
                    <div>
                        <p className="text-center font-weight-bold mb-0">to pay for overdue instalment</p>
                        <p className="text-center pt-2">All figures include Insurance Premium Tax</p>
                    </div>
                </div>
                <PanelDashed className="shadow bg-white text-center pay-by-card-panel">
                    <div className="panel-dashed-contents card-information">{this.renderCardInformation(mainCard)}</div>
                    {!cardExpired && <div className="pb-2 or-divider"> OR</div>}
                    {!cardExpired && <Link id="payNow" className="text-decoration-underline cursor-pointer text-blue-1 muli-bold-16"
                            aria-label={"Pay Now"}
                            state={{
                                verifyCardFailed: false,
                                product
                            }}
                            to={`/dashboard/account/missedPolicyPayment/${this.state.policy?.policyNumber}/${this.props.params.invoiceId ?? ""}`}>
                        Use a different card</Link>}
                    {!!cardExpired &&
                        <div className="mt-3">
                            <p className="text-danger"><img className="d-inline" src="/img/card-expiry-warn-icon.svg" alt="Card expired" />Your card has expired <br />Please update your payment card</p>
                        </div>}
                    <div className="panel-dashed-contents card-buttons">{this.renderCardsButtons(cardExpired, mainCard)}</div>
                </PanelDashed>
                <button type="button"
                        className="btn btn-default btn-white-dashboard m-auto"
                        onClick={this.navigateTo(this.getRedirectUrl())}>
                    Cancel changes
                </button>
                {this.pluginTemplates.renderBackReloadLink("Back", true, this.getRedirectUrl())}
            </div>
        );
    }

    private readonly renderCardInformation = (card: PaymentCard | null): React.ReactNode => {
        if (card) {
            return (
                <div className="d-flex justify-content-center mt-3">
                    <div className="card-details-wrapper">
                        <div>
                            <span className="pr-2">{this.renderCardImage(card)}</span>&nbsp;
                        </div>
                        <div>
                            <div>
                                <span>{this.renderCardTypeName(card)}</span>&nbsp;
                                <span>ending in <b>{card.cardNumberObfuscated.split('******')[1]}</b></span>
                            </div>
                            <div>
                                {this.renderExpiryDate(card)}
                            </div>
                        </div>
                    </div>
                </div>
            );
        } else {
            return <React.Fragment/>
        }
    };

    private readonly renderExpiryDate = (card: PaymentCard): React.ReactNode => {
        const monthFormatter = new Intl.DateTimeFormat('en', {
            month: 'short'
        })
        const cardExpired = isCardExpired(card);
        const textStyle = cardExpired ? "text-danger" : "";
        return <>
            <span className={textStyle}>Expiry date: </span>
            <span className={textStyle}>{monthFormatter.format(new Date(card.expiryYear, card.expiryMonth - 1, 1)) + " '" + card.expiryYear}</span>
        </>;
    }

    private readonly renderCardImage = (card: PaymentCard): React.ReactNode => {
        if (card.cardType.includes("VISA")) {
            return <img className="payment-icon" alt="Visa icon" src="/img/visa-icon.svg"/>;
        }
        if (card.cardType.includes("MASTERCARD")) {
            return <img className="payment-icon" alt="Mastercard icon" src="/img/mastercard-icon.svg"/>;
        }
        return <React.Fragment/>;
    };

    private readonly renderCardTypeName = (card: PaymentCard): string => {
        if (card.cardType.includes("VISA")) {
            return "Visa";
        }
        if (card.cardType.includes("MASTERCARD")) {
            return "Mastercard";
        }
        return "";
    };

    private findMainPaymentCard(): PaymentCard | null {
        const {party} = this.state;
        if (!party) {
            return null;
        }

        const mainCard = findCardToReuse(party, this.state.reuseCard, this.state.mainCardId);
        return mainCard ?? null;
    }

    private renderCardsButtons(forceUpdateCard: boolean | undefined, card?: PaymentCard | null): React.ReactNode {
        const cardsButtons: any = [];
        if (card) {
            const payButtonLabel = forceUpdateCard ? "Update payment card" : `Pay  ${getAmountToDisplay(this.state.amountToPay)}`;
            cardsButtons.push(
                <button key={card.cardNumberObfuscated}
                        className="btn btn-primary btn-make-payment m-auto margin-top-17 justify-content-center"
                        onClick={(event) => this.handleOnClick(event, forceUpdateCard)}
                        id={"card-" + card.cardNumberObfuscated}
                        aria-label={payButtonLabel}
                >
                    {payButtonLabel}
                    <span className="payment-lock-image-raw"/>
                </button>
            );
        }

        return cardsButtons;
    };

    private handleOnClick(event: any, forceUpdateCard: boolean | undefined) : void {
        if (forceUpdateCard) {
            sessionStorage.setItem("directRedirectAfterCardSave", this.props.location.pathname);
            this.props.navigate(`/dashboard/account/changeCard/${this.state.policy?.policyNumber}`)
        } else {
            window.sessionStorage.setItem("paymentAmount", getAmountToDisplay(this.state.amountToPay));
            window.location.assign(`/dashboard/account/paymentProcessing/${null}/${this.state.policy?.policyNumber}${this.props.params.invoiceId ? "/" + this.props.params.invoiceId : ""}`);
        }
        event.preventDefault();
    }

    private navigateTo(link: string) {
        return () => {
            window.location.href = link;
        }
    }
}

export const PayOverdueInstalmentPage = withRouter<{}, IParams>(PayOverdueInstalmentPagePlain)
